import type {ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {Mic, MicOff, MonitorUp, SlidersHorizontal, UserPlus, Users, Video, VideoOff} from 'lucide-react';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import type {ControlTone} from '../components/controls';
import type {GroupCallLeg} from '../model/types';
import {groupActions} from './groupActions';
import {groupSurfaceMessages as m} from './groupMessages';

export interface ControlSpec {
    readonly id: string;
    readonly icon: ReactNode;
    readonly label: string;
    readonly caption: string;
    readonly tone: ControlTone;
    readonly alert?: boolean;
    readonly badge?: number;
    readonly disabled?: boolean;
    readonly pressed?: boolean;
    readonly expanded?: boolean;
    readonly hasPopup?: 'dialog' | 'menu';
    readonly testId: string;
    readonly onClick: () => void;
}

interface GroupControlOptions {
    readonly iconSize: number;
    readonly busy: boolean;
    readonly peopleOpen: boolean;
    readonly otherSharer?: string;
    readonly onTogglePeople: () => void;
    readonly onOpenShare: () => void;
    readonly onOpenDevices: () => void;
    readonly onHowToAllow: () => void;
    readonly onAddPeople?: () => void;
}

export interface GroupControls {
    readonly microphone: ControlSpec;
    readonly camera?: ControlSpec;
    readonly share?: ControlSpec;
    readonly people: ControlSpec;
    readonly add?: ControlSpec;
    readonly devices: ControlSpec;
}

export const useGroupControls = (leg: GroupCallLeg, options: GroupControlOptions): GroupControls => {
    const intl = useIntl();
    const {iconSize, busy, peopleOpen, otherSharer} = options;
    const {microphoneEnabled, cameraEnabled, screenSharing, deviceIssues} = leg.local;
    const icon = (node: (size: number) => ReactNode) => node(iconSize);

    const microphone = ((): ControlSpec => {
        const base = {id: 'microphone', testId: 'group-call-mic', disabled: busy};
        switch (deviceIssues?.microphone) {
            case 'blocked':
                return {
                    ...base,
                    icon: icon(size => <MicOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(m.micBlockedAria),
                    caption: intl.formatMessage(m.captionMicBlocked),
                    tone: 'error',
                    alert: true,
                    disabled: false,
                    hasPopup: 'dialog',
                    onClick: options.onHowToAllow,
                };
            case 'not_found':
                return {
                    ...base,
                    icon: icon(size => <MicOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(m.noMicrophoneAria),
                    caption: intl.formatMessage(m.captionNoMic),
                    tone: 'unavailable',
                    hasPopup: 'dialog',
                    onClick: options.onOpenDevices,
                };
            case 'in_use':
            case 'failed':
                return {
                    ...base,
                    icon: icon(size => <MicOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(m.retryMicrophoneAria),
                    caption: intl.formatMessage(m.captionTryAgain),
                    tone: 'warning',
                    alert: true,
                    onClick: () => groupActions.setMicrophone(true),
                };
            default:
                return {
                    ...base,
                    icon: microphoneEnabled ? icon(size => <Mic size={size} aria-hidden="true"/>) : icon(size => <MicOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(microphoneEnabled ? gm.controlMute : gm.controlUnmute),
                    caption: intl.formatMessage(microphoneEnabled ? m.captionMute : m.captionUnmute),
                    tone: microphoneEnabled ? 'default' : 'pressed',
                    onClick: () => groupActions.setMicrophone(!microphoneEnabled),
                };
        }
    })();

    const camera = ((): ControlSpec | undefined => {
        if (!leg.capabilities.has('camera')) {
            return undefined;
        }
        const base = {id: 'camera', testId: 'group-call-camera', disabled: busy};
        switch (deviceIssues?.camera) {
            case 'blocked':
                return {
                    ...base,
                    icon: icon(size => <VideoOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(m.cameraBlockedAria),
                    caption: intl.formatMessage(m.captionCameraBlocked),
                    tone: 'error',
                    alert: true,
                    disabled: false,
                    hasPopup: 'dialog',
                    onClick: options.onHowToAllow,
                };
            case 'not_found':
                return {
                    ...base,
                    icon: icon(size => <VideoOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(m.cameraMissingAria),
                    caption: intl.formatMessage(m.captionNoCamera),
                    tone: 'unavailable',
                    hasPopup: 'dialog',
                    onClick: options.onOpenDevices,
                };
            case 'in_use':
            case 'failed':
                return {
                    ...base,
                    icon: icon(size => <VideoOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(m.retryCameraAria),
                    caption: intl.formatMessage(m.captionTryAgain),
                    tone: 'warning',
                    alert: true,
                    onClick: () => groupActions.setCamera(true),
                };
            default:
                return {
                    ...base,
                    icon: cameraEnabled ? icon(size => <Video size={size} aria-hidden="true"/>) : icon(size => <VideoOff size={size} aria-hidden="true"/>),
                    label: intl.formatMessage(cameraEnabled ? gm.controlCameraOff : gm.controlCameraOn),
                    caption: intl.formatMessage(m.captionCamera),
                    tone: cameraEnabled ? 'pressed' : 'default',
                    onClick: () => groupActions.setCamera(!cameraEnabled),
                };
        }
    })();

    const share = leg.capabilities.has('share')
        ? {
              id: 'share',
              testId: 'group-call-screen-share',
              icon: icon(size => <MonitorUp size={size} aria-hidden="true"/>),
              label: otherSharer && !screenSharing
                  ? intl.formatMessage(m.shareBusyAria, {name: otherSharer})
                  : intl.formatMessage(screenSharing ? gm.controlStopSharing : gm.controlShareScreen),
              caption: intl.formatMessage(m.captionShare),
              tone: (screenSharing ? 'pressed' : 'default') as ControlTone,
              disabled: busy || (!!otherSharer && !screenSharing),
              hasPopup: screenSharing ? undefined : ('dialog' as const),
              onClick: screenSharing ? groupActions.stopScreenShare : options.onOpenShare,
          }
        : undefined;

    const people: ControlSpec = {
        id: 'people',
        testId: 'group-call-participants',
        icon: icon(size => <Users size={size} aria-hidden="true"/>),
        label: intl.formatMessage(gm.controlParticipants),
        caption: intl.formatMessage(m.captionPeople),
        tone: peopleOpen ? 'brand' : 'default',
        badge: leg.participantCount,
        pressed: peopleOpen,
        onClick: options.onTogglePeople,
    };

    const devices: ControlSpec = {
        id: 'devices',
        testId: 'group-call-settings',
        icon: icon(size => <SlidersHorizontal size={size} aria-hidden="true"/>),
        label: intl.formatMessage(m.deviceSettings),
        caption: intl.formatMessage(m.captionDevices),
        tone: 'default',
        hasPopup: 'dialog',
        onClick: options.onOpenDevices,
    };

    const add: ControlSpec | undefined = options.onAddPeople
        ? {
              id: 'add',
              testId: 'group-call-add-people',
              icon: icon(size => <UserPlus size={size} aria-hidden="true"/>),
              label: intl.formatMessage(m.addPeople),
              caption: intl.formatMessage(m.captionAdd),
              tone: 'default',
              disabled: busy,
              onClick: options.onAddPeople,
          }
        : undefined;

    return {microphone, camera, share, people, add, devices};
};
